import { AutoComplete, Image } from 'antd';
import { Spinner } from '../assets';
import InputField from '../generic/InputField/InputField';
import { gql, useQuery } from '@apollo/client';
import { useCallback, useEffect, useState } from 'react';
import { getOrganization } from '../../utils/config';
import { debounce } from 'lodash';
import unknownUserImg from '../assets/images/unknown-user.webp';

import FetchError from '../generic/FetchError';

type UserAutocompletionProps = {
  watch?: any;
  reset: any;
  register: any;
  errors: any;
  control: any;
  currentData: any;
  isSectionOwner?: boolean;
  editable?: boolean;
};

const FIND_USERS = gql`
  query SearchUser($data: SearchUserInput!) {
    searchUser(data: $data) {
      user_id
      name
      email
      picture
    }
  }
`;

const UserAutocompletion = ({
  watch,
  reset,
  register,
  errors,
  control,
  currentData,
  isSectionOwner,
  editable = false,
}: UserAutocompletionProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const organizationId = getOrganization();

  const autoCompletionDefault = currentData
    ? [
        `${
          currentData.sectionOwnerfirstName ??
          currentData.contactFirstName ??
          currentData.internalContactFirstName ??
          currentData.firstName ??
          currentData.planOwnerFirstName ??
          ''
        } ${
          currentData.sectionOwnerlastName ??
          currentData.contactLastName ??
          currentData.internalContactLastName ??
          currentData.lastName ??
          currentData.planOwnerLastName ??
          ''
        }`.trim(),

        currentData.email ?? currentData.internalEmail ?? '',
      ]
        .filter(Boolean)
        .join('; ')
    : '';

  const email = watch('email');

  const {
    data: users,
    loading: usersLoading,
    error: usersError,
  } = useQuery(FIND_USERS, {
    variables: {
      data: {
        search: searchTerm,
        organizationId: organizationId,
      },
    },
  });

  const usersData = users?.searchUser || [];

  const profilePicture = usersData?.find((user: any) => user.email === email)
    ?.picture;

  const handlePopulateFields = (value: string) => {
    if (!value) return;

    const [fullName, email = ''] = value.split(';').map((v) => v.trim());

    let [firstName = '', lastName = '', _] = fullName.split(/[,.\s@]+/);

    reset((prev: any) => ({
      ...prev,
      sectionOwnerfirstName: firstName,
      sectionOwnerlastName: lastName,
      email: email.trim(),
    }));
  };

  const debouncedSearch = useCallback(
    debounce((text) => {
      setSearchTerm(text);
    }, 300),
    [],
  );

  const handleSearch = (value: string) => {
    debouncedSearch(value);
  };

  useEffect(() => {
    if (!currentData) return;
    const defaultValues = {
      sectionOwnerfirstName:
        currentData.sectionOwnerfirstName ??
        currentData.contactFirstName ??
        currentData?.internalContactFirstName ??
        currentData.firstName ??
        currentData?.planOwnerFirstName ??
        '',
      sectionOwnerlastName:
        currentData.sectionOwnerlastName ??
        currentData.contactLastName ??
        currentData?.internalContactLastName ??
        currentData.lastName ??
        currentData?.planOwnerLastName ??
        '',
      email: currentData?.email ?? currentData?.internalEmail ?? '',
    };

    const trimmedData = {
      ...defaultValues,
      sectionOwnerfirstName: defaultValues.sectionOwnerfirstName.trim(),
      sectionOwnerlastName: defaultValues.sectionOwnerlastName.trim(),
    };

    if (isSectionOwner) {
      Object.assign(trimmedData, {
        sectionRequired: currentData?.sectionRequired ? 'true' : 'false',
      });
    }

    reset((prev: any) => ({ ...prev, ...trimmedData }));
  }, [currentData]);

  return (
    <>
      <div className="flex items-center gap-2 mb-2.5">
        <div className="w-10 h-10">
          {!usersLoading ? (
            <Image
              src={profilePicture || unknownUserImg}
              alt="Profile Picture"
              className="w-full h-full rounded-full"
              loading="lazy"
            />
          ) : (
            <Spinner width={40} height={40} />
          )}
        </div>
        <AutoComplete
          className="w-full flex-1"
          options={
            usersData?.map((user: any) => {
              const { name, email } = user || {};
              return {
                value: `${name}; ${email}`,
                label: name,
              };
            }) || []
          }
          placeholder="Search for a user"
          onSearch={handleSearch}
          onSelect={(values) => handlePopulateFields(values)}
          defaultValue={autoCompletionDefault.trim()}
        />
      </div>
      <InputField
        label={
          isSectionOwner ? 'Section Owner First Name' : 'Contact First Name'
        }
        name="sectionOwnerfirstName"
        rules={{ required: false }}
        errors={errors?.sectionOwnerfirstName}
        errorMessage="This field is required."
        register={register}
        control={control}
        spaceBetween
        disabled={!editable}
        defaultValue={
          currentData?.sectionOwnerfirstName || currentData?.contactFirstName
        }
      />
      <InputField
        label={isSectionOwner ? 'Section Owner Last Name' : 'Contact Last Name'}
        name="sectionOwnerlastName"
        rules={{ required: false }}
        errors={errors?.sectionOwnerlastName}
        errorMessage="This field is required."
        register={register}
        control={control}
        spaceBetween
        disabled={!editable}
        defaultValue={
          currentData?.sectionOwnerlastName || currentData?.contactLastName
        }
      />
      <InputField
        type="email"
        label="Email"
        name="email"
        rules={{ required: false }}
        errors={errors.email}
        errorMessage="This field is required."
        register={register}
        control={control}
        spaceBetween
        disabled={!editable}
        defaultValue={currentData?.email}
      />
    </>
  );
};

export default UserAutocompletion;
